import type { AnimalItem, ShareItem } from "../mocks/animals";
import type { CustomerItem } from "../mocks/customers";
import type { PaymentRecord } from "../mocks/payments";
import type { IncomeExpenseEntry } from "./incomeExpenseStore";
import type { UserRecord } from "../store/usersStore";
import type { DeliveryAssignment } from "./deliveryStore";
import type { SlaughterOpRecord } from "./slaughterOpsStore";
import { getDisplayKupeNo, getDisplaySiparisNo } from "./animalLabels";
import { getShareTotal } from "./cascadeOps";

export type ReportQuickPreset =
  | "all"
  | "today-sales"
  | "today-payments"
  | "debtors"
  | "transport-awaiting"
  | "unsold-animals";

export interface ReportFilters {
  search: string;
  kupeNo: string;
  customer: string;
  user: string;
  salesType: "Tümü" | "Hisseli" | "Hissesiz" | "Dış Kesim";
  paymentStatus: "Tümü" | "Ödendi" | "Borçlu" | "Eksi Bakiye";
  noteMode: "Tümü" | "Not Var" | "Not Yok";
  dateFrom: string;
  dateTo: string;
  minSale: string;
  maxSale: string;
  minRemaining: string;
  maxRemaining: string;
  preset: ReportQuickPreset;
}

export interface SaleReportRow {
  animalId: number;
  shareId: number;
  customerId?: number;
  customerName: string;
  phone?: string;
  kupeNo: string;
  siparisNo: string;
  animalType: "Büyükbaş" | "Küçükbaş";
  saleType: "Hisseli" | "Hissesiz" | "Dış Kesim";
  shareNo: string;
  shareCount: number;
  saleAmount: number;
  paidAmount: number;
  remainingAmount: number;
  saleDate: string;
  note: string;
  receiptNo: string;
  kesimFee: number;
  parcalamaFee: number;
  tasimaFee: number;
  totalServiceFee: number;
  operatorName: string;
}

export interface CustomerDebtRow {
  customerId: number;
  customerName: string;
  phone: string;
  totalSale: number;
  totalPaid: number;
  totalRemaining: number;
  shareCount: number;
  lastReceiptNo: string;
  noteCount: number;
}

export interface UserPerformanceRow {
  userName: string;
  role: string;
  createdCustomers: number;
  paymentCount: number;
  paymentAmount: number;
}

export interface ReportsAggregateResult {
  filteredSales: SaleReportRow[];
  filteredPayments: PaymentRecord[];
  filteredCustomers: CustomerDebtRow[];
  filteredAnimals: AnimalItem[];
  filteredIncomeExpense: IncomeExpenseEntry[];
  overview: {
    totalAnimals: number;
    totalShares: number;
    soldShares: number;
    emptyShares: number;
    totalSale: number;
    totalPaid: number;
    totalRemaining: number;
    totalIncome: number;
    totalExpense: number;
    profit: number;
    slaughterWaiting: number;
    packingWaiting: number;
    transportWaiting: number;
    deliveredCount: number;
    disKesimCount: number;
  };
  salesByType: Array<{ label: string; value: number; amount: number }>;
  topCustomers: CustomerDebtRow[];
  paymentSummary: {
    todayAmount: number;
    paymentCount: number;
    debtors: number;
    negativeBalanceCount: number;
  };
  operationSummary: {
    queued: number;
    entered: number;
    cut: number;
    packing: number;
    done: number;
    readyForDelivery: number;
    onDelivery: number;
    delivered: number;
  };
  topUsers: UserPerformanceRow[];
}

function lower(value?: string | number | null) {
  return String(value ?? "").toLocaleLowerCase("tr-TR");
}

function normalizeDate(value?: string) {
  return String(value ?? "").slice(0, 10);
}

function isWithinDateRange(value: string, from: string, to: string) {
  if (!value) return true;
  if (from && value < from) return false;
  if (to && value > to) return false;
  return true;
}

function toNumber(value: string) {
  const cleaned = String(value ?? "").replace(/\./g, "").replace(",", ".").trim();
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function deriveSaleType(animal: AnimalItem): "Hisseli" | "Hissesiz" | "Dış Kesim" {
  if ((animal as AnimalItem & { disKesim?: boolean }).disKesim) return "Dış Kesim";
  return animal.hisseliSatisEtiketi ? "Hisseli" : "Hissesiz";
}

function hasText(value?: string) {
  return String(value ?? "").trim().length > 0;
}

function hasAnyShareNote(share: ShareItem) {
  return hasText(share.satisNotu) || hasText(share.pesinatAciklama);
}

export function createDefaultReportFilters(): ReportFilters {
  const today = new Date().toISOString().slice(0, 10);
  return {
    search: "",
    kupeNo: "",
    customer: "",
    user: "",
    salesType: "Tümü",
    paymentStatus: "Tümü",
    noteMode: "Tümü",
    dateFrom: "",
    dateTo: "",
    minSale: "",
    maxSale: "",
    minRemaining: "",
    maxRemaining: "",
    preset: "all",
  };
}

export function aggregateReports(params: {
  animals: AnimalItem[];
  customers: CustomerItem[];
  payments: PaymentRecord[];
  incomeExpenseEntries: IncomeExpenseEntry[];
  users: UserRecord[];
  deliveries: DeliveryAssignment[];
  slaughterOps: SlaughterOpRecord[];
  filters: ReportFilters;
}): ReportsAggregateResult {
  const {
    animals,
    customers,
    payments,
    incomeExpenseEntries,
    users,
    deliveries,
    slaughterOps,
    filters,
  } = params;

  const salesRows: SaleReportRow[] = animals.flatMap((animal) =>
    animal.hisseler
      .filter((share) => share.musteriAdi.trim().length > 0 || share.tutar > 0 || share.odenen > 0)
      .map((share) => ({
        animalId: animal.id,
        shareId: share.id,
        customerId: share.musteriId,
        customerName: share.musteriAdi || "-",
        phone: share.telefon,
        kupeNo: getDisplayKupeNo(animal),
        siparisNo: getDisplaySiparisNo(animal),
        animalType: animal.tur,
        saleType: deriveSaleType(animal),
        shareNo: share.hisseNo,
        shareCount: share.hisseSayisi,
        saleAmount: getShareTotal(share),
        paidAmount: Number(share.odenen ?? 0),
        remainingAmount: Number(share.kalan ?? 0),
        saleDate: normalizeDate(share.satisTarihi),
        note: String(share.satisNotu ?? share.pesinatAciklama ?? "").trim(),
        receiptNo: String(share.satisFisNo ?? "").trim(),
        kesimFee: Number(share.kesimUcreti ?? 0),
        parcalamaFee: Number(share.parcalamaUcreti ?? 0),
        tasimaFee: Number(share.tasimaUcreti ?? 0),
        totalServiceFee:
          Number(share.kesimUcreti ?? 0) +
          Number(share.parcalamaUcreti ?? 0) +
          Number(share.tasimaUcreti ?? 0),
        operatorName: String(share.satisFisNo ? "Fişli Kayıt" : "Panel"),
      }))
  );

  const saleMin = toNumber(filters.minSale);
  const saleMax = toNumber(filters.maxSale);
  const remainingMin = toNumber(filters.minRemaining);
  const remainingMax = toNumber(filters.maxRemaining);

  const presetToday = new Date().toISOString().slice(0, 10);

  const filteredSales = salesRows.filter((row) => {
    const searchable = [
      row.customerName,
      row.phone,
      row.kupeNo,
      row.siparisNo,
      row.shareNo,
      row.note,
      row.receiptNo,
    ]
      .join(" ")
      .toLocaleLowerCase("tr-TR");

    if (filters.search.trim() && !searchable.includes(lower(filters.search))) return false;
    if (filters.kupeNo.trim() && row.kupeNo !== filters.kupeNo.trim()) return false;
    if (filters.customer.trim() && !lower(row.customerName).includes(lower(filters.customer))) {
      return false;
    }
    if (filters.salesType !== "Tümü" && row.saleType !== filters.salesType) return false;

    if (filters.noteMode === "Not Var" && !hasText(row.note)) return false;
    if (filters.noteMode === "Not Yok" && hasText(row.note)) return false;

    if (!isWithinDateRange(row.saleDate, filters.dateFrom, filters.dateTo)) return false;
    if (saleMin !== null && row.saleAmount < saleMin) return false;
    if (saleMax !== null && row.saleAmount > saleMax) return false;
    if (remainingMin !== null && row.remainingAmount < remainingMin) return false;
    if (remainingMax !== null && row.remainingAmount > remainingMax) return false;

    if (filters.paymentStatus === "Ödendi" && row.remainingAmount > 0) return false;
    if (filters.paymentStatus === "Borçlu" && row.remainingAmount <= 0) return false;
    if (filters.paymentStatus === "Eksi Bakiye" && row.remainingAmount >= 0) return false;

    if (filters.preset === "today-sales" && row.saleDate !== presetToday) return false;
    if (filters.preset === "debtors" && row.remainingAmount <= 0) return false;
    if (filters.preset === "unsold-animals") return false;

    return true;
  });

  const filteredAnimals = animals.filter((animal) => {
    const animalSales = filteredSales.filter((row) => row.animalId === animal.id);
    const displayKupeNo = getDisplayKupeNo(animal);
    const displaySiparisNo = getDisplaySiparisNo(animal);
    const statusText = [displayKupeNo, displaySiparisNo, animal.tur, animal.kesimGunu].join(" ").toLocaleLowerCase("tr-TR");

    if (filters.search.trim() && !statusText.includes(lower(filters.search)) && animalSales.length === 0) {
      return false;
    }
    if (filters.kupeNo.trim() && displayKupeNo !== filters.kupeNo.trim()) return false;
    if (filters.salesType !== "Tümü" && deriveSaleType(animal) !== filters.salesType) return false;
    if (filters.noteMode === "Not Var" && !animal.hisseler.some(hasAnyShareNote)) return false;
    if (filters.noteMode === "Not Yok" && animal.hisseler.some(hasAnyShareNote)) return false;
    if (filters.preset === "unsold-animals") {
      return animal.hisseler.every((share) => !hasText(share.musteriAdi));
    }
    return true;
  });

  const filteredPayments = payments.filter((payment) => {
    const searchable = [
      payment.musteriAdi,
      payment.telefon,
      payment.hayvanKupeNo,
      payment.hayvanSiparisNo,
      payment.fisNo,
      payment.aciklama,
      payment.olusturan,
    ]
      .join(" ")
      .toLocaleLowerCase("tr-TR");

    if (filters.search.trim() && !searchable.includes(lower(filters.search))) return false;
    if (
      filters.kupeNo.trim() &&
      payment.hayvanKupeNo !== filters.kupeNo.trim() &&
      payment.hayvanSiparisNo !== filters.kupeNo.trim()
    ) return false;
    if (filters.customer.trim() && !lower(payment.musteriAdi).includes(lower(filters.customer))) {
      return false;
    }
    if (filters.user.trim() && !lower(payment.olusturan).includes(lower(filters.user))) {
      return false;
    }
    if (!isWithinDateRange(normalizeDate(payment.tarih), filters.dateFrom, filters.dateTo)) {
      return false;
    }
    if (filters.preset === "today-payments" && normalizeDate(payment.tarih) !== presetToday) {
      return false;
    }
    return true;
  });

  const customerRows: CustomerDebtRow[] = customers
    .map((customer) => {
      const customerSales = filteredSales.filter(
        (row) => row.customerId === customer.id || row.customerName === customer.adSoyad
      );

      return {
        customerId: customer.id,
        customerName: customer.adSoyad,
        phone: customer.telefon,
        totalSale: customerSales.reduce((sum, row) => sum + row.saleAmount, 0),
        totalPaid: customerSales.reduce((sum, row) => sum + row.paidAmount, 0),
        totalRemaining: customerSales.reduce((sum, row) => sum + row.remainingAmount, 0),
        shareCount: customerSales.reduce((sum, row) => sum + row.shareCount, 0),
        lastReceiptNo: customerSales.find((row) => hasText(row.receiptNo))?.receiptNo ?? "-",
        noteCount: customerSales.filter((row) => hasText(row.note)).length,
      };
    })
    .filter((row) => {
      if (filters.customer.trim() && !lower(row.customerName).includes(lower(filters.customer))) return false;
      if (filters.paymentStatus === "Borçlu" && row.totalRemaining <= 0) return false;
      if (filters.paymentStatus === "Ödendi" && row.totalRemaining > 0) return false;
      if (filters.noteMode === "Not Var" && row.noteCount === 0) return false;
      if (filters.noteMode === "Not Yok" && row.noteCount > 0) return false;
      if (filters.preset === "debtors" && row.totalRemaining <= 0) return false;
      return row.totalSale > 0 || row.totalPaid > 0 || row.totalRemaining > 0;
    })
    .sort((a, b) => b.totalRemaining - a.totalRemaining);

  const filteredIncomeExpense = incomeExpenseEntries.filter((entry) =>
    isWithinDateRange(normalizeDate(entry.tarih), filters.dateFrom, filters.dateTo)
  );

  const slaughterMap = new Map(slaughterOps.map((item) => [item.animalId, item.status]));

  const overview = {
    totalAnimals: filteredAnimals.length,
    totalShares: filteredAnimals.reduce((sum, animal) => sum + animal.toplamHisse, 0),
    soldShares: filteredAnimals.reduce(
      (sum, animal) => sum + animal.hisseler.filter((share) => hasText(share.musteriAdi)).length,
      0
    ),
    emptyShares: filteredAnimals.reduce(
      (sum, animal) => sum + animal.hisseler.filter((share) => !hasText(share.musteriAdi)).length,
      0
    ),
    totalSale: filteredSales.reduce((sum, row) => sum + row.saleAmount, 0),
    totalPaid: filteredSales.reduce((sum, row) => sum + row.paidAmount, 0),
    totalRemaining: filteredSales.reduce((sum, row) => sum + row.remainingAmount, 0),
    totalIncome: filteredIncomeExpense.filter((row) => row.tur === "Gelir").reduce((sum, row) => sum + row.tutar, 0),
    totalExpense: filteredIncomeExpense.filter((row) => row.tur === "Gider").reduce((sum, row) => sum + row.tutar, 0),
    profit:
      filteredIncomeExpense.filter((row) => row.tur === "Gelir").reduce((sum, row) => sum + row.tutar, 0) -
      filteredIncomeExpense.filter((row) => row.tur === "Gider").reduce((sum, row) => sum + row.tutar, 0),
    slaughterWaiting: filteredAnimals.filter((animal) => slaughterMap.get(animal.id) === "queued").length,
    packingWaiting: filteredAnimals.filter((animal) => slaughterMap.get(animal.id) === "packing").length,
    transportWaiting: deliveries.filter((item) => item.status === "Hazır").length,
    deliveredCount: deliveries.filter((item) => item.status === "Teslim Edildi").length,
    disKesimCount: filteredAnimals.filter((animal) => deriveSaleType(animal) === "Dış Kesim").length,
  };

  const salesByType = ["Hisseli", "Hissesiz", "Dış Kesim"].map((label) => {
    const rows = filteredSales.filter((row) => row.saleType === label);
    return {
      label,
      value: rows.length,
      amount: rows.reduce((sum, row) => sum + row.saleAmount, 0),
    };
  });

  const paymentSummary = {
    todayAmount: filteredPayments
      .filter((payment) => normalizeDate(payment.tarih) === presetToday)
      .reduce((sum, payment) => sum + Number(payment.tutar ?? 0), 0),
    paymentCount: filteredPayments.length,
    debtors: customerRows.filter((row) => row.totalRemaining > 0).length,
    negativeBalanceCount: customerRows.filter((row) => row.totalRemaining < 0).length,
  };

  const operationSummary = {
    queued: filteredAnimals.filter((animal) => slaughterMap.get(animal.id) === "queued").length,
    entered: filteredAnimals.filter((animal) => slaughterMap.get(animal.id) === "entered").length,
    cut: filteredAnimals.filter((animal) => slaughterMap.get(animal.id) === "cut").length,
    packing: filteredAnimals.filter((animal) => slaughterMap.get(animal.id) === "packing").length,
    done: filteredAnimals.filter((animal) => slaughterMap.get(animal.id) === "done").length,
    readyForDelivery: deliveries.filter((item) => item.status === "Hazır").length,
    onDelivery: deliveries.filter((item) => item.status === "Taşıma Halinde").length,
    delivered: deliveries.filter((item) => item.status === "Teslim Edildi").length,
  };

  const userCreateMap = new Map<number, number>();
  customerRows.forEach((row) => {
    const match = users.find((user) => user.customerId === row.customerId);
    if (match) {
      userCreateMap.set(match.id, (userCreateMap.get(match.id) ?? 0) + 1);
    }
  });

  const topUsers: UserPerformanceRow[] = users
    .filter((user) => user.rol !== "Müşteri")
    .map((user) => {
      const paymentRows = filteredPayments.filter((payment) => lower(payment.olusturan) === lower(user.adSoyad));
      return {
        userName: user.adSoyad,
        role: user.rol,
        createdCustomers: userCreateMap.get(user.id) ?? 0,
        paymentCount: paymentRows.length,
        paymentAmount: paymentRows.reduce((sum, payment) => sum + Number(payment.tutar ?? 0), 0),
      };
    })
    .sort((a, b) => b.paymentAmount - a.paymentAmount);

  return {
    filteredSales,
    filteredPayments,
    filteredCustomers: customerRows,
    filteredAnimals,
    filteredIncomeExpense,
    overview,
    salesByType,
    topCustomers: customerRows.slice(0, 8),
    paymentSummary,
    operationSummary,
    topUsers: topUsers.slice(0, 8),
  };
}
